import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Box, IconButton, Typography } from "@mui/material";
import {
	CHANGE_SUGGESTION_COLORS,
	CHANGE_SUGGESTION_LABELS,
	type ChangeSuggestion,
} from "../models/NextCampaigns";

const LEGEND_BORDER_COLORS: Record<ChangeSuggestion, string> = {
	K: "#bdbdbd",
	C: "#66bb6a",
	N: "#ef5350",
	P: "#42a5f5",
};

const LEGEND_ITEMS: ChangeSuggestion[] = ["K", "P", "C", "N"];

interface Props {
	page: number;
	totalPages: number;
	onPageChange: (page: number) => void;
}

const pageButtonSx = {
	bgcolor: "primary.main",
	color: "white",
	width: 28,
	height: 28,
	"&:hover": { bgcolor: "primary.dark" },
	"&.Mui-disabled": {
		bgcolor: "action.disabledBackground",
		color: "action.disabled",
	},
};

export const LegendBar = ({ page, totalPages, onPageChange }: Props) => {
	const isFirstPage = page === 0;
	const isLastPage = page >= totalPages - 1;

	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 1.5,
				px: 2,
				py: 0.75,
			}}
		>
			{/* Left: Legend */}
			<Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
				{LEGEND_ITEMS.map((code) => (
					<Box
						key={code}
						sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
					>
						<Box
							sx={{
								width: 20,
								height: 20,
								borderRadius: 0.5,
								backgroundColor:
									code === "K" ? "#ffffff" : CHANGE_SUGGESTION_COLORS[code],
								border: `1.5px solid ${LEGEND_BORDER_COLORS[code]}`,
							}}
						/>
						<Typography
							variant="body2"
							sx={{ color: "text.secondary", fontSize: "13px" }}
						>
							{CHANGE_SUGGESTION_LABELS[code]}
						</Typography>
					</Box>
				))}
			</Box>

			{/* Right: Pagination */}
			{totalPages > 1 && (
				<Box
					sx={{
						ml: "auto",
						display: "flex",
						alignItems: "center",
						gap: 0.5,
					}}
				>
					<IconButton
						onClick={() => onPageChange(0)}
						disabled={isFirstPage}
						size="small"
						sx={pageButtonSx}
					>
						<FirstPageIcon fontSize="small" />
					</IconButton>
					<IconButton
						onClick={() => onPageChange(page - 1)}
						disabled={isFirstPage}
						size="small"
						sx={pageButtonSx}
					>
						<NavigateBeforeIcon fontSize="small" />
					</IconButton>
					<Typography
						variant="body2"
						sx={{
							minWidth: 40,
							textAlign: "center",
							fontWeight: 600,
							fontSize: "13px",
							userSelect: "none",
						}}
					>
						{page + 1} / {totalPages}
					</Typography>
					<IconButton
						onClick={() => onPageChange(page + 1)}
						disabled={isLastPage}
						size="small"
						sx={pageButtonSx}
					>
						<NavigateNextIcon fontSize="small" />
					</IconButton>
					<IconButton
						onClick={() => onPageChange(totalPages - 1)}
						disabled={isLastPage}
						size="small"
						sx={pageButtonSx}
					>
						<LastPageIcon fontSize="small" />
					</IconButton>
				</Box>
			)}
		</Box>
	);
};
